/**
 * Misquote — Paper Studio shared primitives.
 *
 * Every screen of the redesigned game is composed from these pieces:
 * paper-textured frames, deckle-edged cards, tape strips, red-ink stamps,
 * typewriter eyebrows, sticky notes, and the headline mask-reveal type.
 */

import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import { ArrowSquareOut } from "@phosphor-icons/react";
import { motion, useReducedMotion } from "framer-motion";
import { forwardRef, isValidElement, useEffect, useRef, useState } from "react";
import type {
    ReactNode, HTMLAttributes, ButtonHTMLAttributes, ComponentType, MouseEvent, Ref,
} from "react";

import { paper, fonts, easing, calmLoop, shade, redA, inkA, paperA, stickyA, gloss, blackA, bp } from "./theme";
import { words as BACKDROP_WORDS } from "./words";
import * as haptics from "./haptics";
import type { HapticName } from "./haptics";

/* ============================================================
   HAPTICS — carried by the controls, not by the screens
   ============================================================ */

/**
 * Every pressable primitive fires its own haptic.
 *
 * The alternative — each screen calling `haptics.x()` inside its click handler
 * — is how the app came to buzz when you copied an invite link but not when you
 * started the game: consistency depended on whoever wrote the screen
 * remembering. A control knows what it is, so the default lives next to the
 * thing being pressed and a screen only speaks up when it wants something other
 * than the default.
 */
export interface HapticProps {
    /** Override this control's default pattern. `null` silences it — needed
     *  where a component drives its own (HoldToConfirmButton) and the click
     *  would otherwise double up. */
    haptic?: HapticName | null;
}

type Pressable = ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Wrap a styled control so a press fires a haptic ahead of the caller's
 * `onClick`. One factory rather than a wrapper per primitive: the defaults
 * differ, the plumbing doesn't.
 *
 * The result is a component rather than a styled component, so `styled(X)` still
 * composes onto it but emotion's `as` prop no longer reaches the underlying tag
 * — `as` would render the substitute element and skip the wrapper's styles
 * entirely. Where a screen needs the same face on a different element, take the
 * exported css block instead (see `msTileFace`).
 */
function hapticControl<P extends Pressable>(
    Base: ComponentType<P>,
    fallback: HapticName | null | ((props: P) => HapticName | null),
    displayName: string,
) {
    const Tag = Base as ComponentType<P & { ref?: Ref<HTMLButtonElement> }>;

    const Control = forwardRef<HTMLButtonElement, P & HapticProps>(
        function HapticControl(allProps, ref) {
            const { haptic, onClick, ...rest } = allProps as P & HapticProps & Pressable;
            // `rest` is P minus two known keys; TS can't prove that's still P.
            const props = rest as unknown as P;
            const pattern = haptic !== undefined
                ? haptic
                : typeof fallback === "function" ? fallback(props) : fallback;

            return (
                <Tag
                    {...props}
                    ref={ref}
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        haptics.fire(pattern);
                        onClick?.(event);
                    }}
                />
            );
        },
    );
    Control.displayName = displayName;
    return Control;
}

/* ============================================================
   SCREEN — paper canvas with grain + soft inner deckle
   ============================================================ */

/**
 * MsScreen is a transparent flex container that fills the viewport. The paper
 * texture and vignette live on body::before in index.css so every screen reads
 * as paper without the screen having to redraw the texture. Horizontal overflow
 * is clipped (the ghost marquee deliberately extends beyond the viewport);
 * on desktop the whole page scrolls vertically instead of the body.
 */
export const MsScreen = styled.div`
    position: relative;
    flex: 1;
    /* Deliberately not a viewport-height minimum. A flex item's automatic
     * minimum size is its content, so a min-height chain grows to fit and the
     * page scrolls as a whole; releasing that floor to zero lets this shrink to
     * the space the shell gives it, which is what makes the body inside it a
     * scroll region instead. A banner above (the spectator strip) then takes its
     * share off the top for free. */
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    color: ${paper.ink};
    font-family: ${fonts.sans};
    isolation: isolate;
    /* clip, not hidden: hidden makes this a scroll container, and iOS WebKit
     * latches touch pans onto the innermost scroll container under the finger —
     * a hidden one swallows the gesture with nothing to scroll, which is how a
     * one-finger scroll goes dead while pinch-zoom (a viewport gesture) still
     * works. clip clips identically but is defined to create no scroll
     * container, so pans fall through to MsBody. */
    overflow: clip;
`;

/**
 * Body and Footer constrain content to a readable column. Phone gets the full
 * viewport; once the paper is wider than ~600px we centre the content with
 * `--content-max` so headlines/cards don't stretch awkwardly.
 *
 * Screens that need a wider layout (lobby on desktop, voting grid, standings)
 * can override `--content-max` with `--wide` via inline style or a wrapper.
 *
 * Top / sides / bottom include safe-area-inset on phone so the first line of
 * type clears the notch and the last CTA clears the home indicator.
 */
interface MsBodyProps {
    /** Opt into a wider content column for screens that need horizontal room
     *  (grids of cards, multi-column layouts). */
    wide?: boolean;
}

export const MsBody = styled.div<MsBodyProps>`
    position: relative;
    z-index: 2;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding:
        calc(22px + var(--safe-top))
        calc(22px + var(--safe-right))
        22px
        calc(22px + var(--safe-left));
    /* The load-bearing line. Growing alone leaves the automatic minimum size
     * the content's height, so this grows instead of scrolling and the overflow
     * rule below never does anything — which is why the header used to scroll
     * away with the rest of the page. */
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    max-width: ${p => p.wide ? "var(--content-max-wide)" : "var(--content-max)"};
    margin: 0 auto;
    overscroll-behavior: contain;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${paper.ruleSoft}; border-radius: 2px; }

    /* Desktop scrolls here too, rather than moving the page: a pinned header and
       footer is the point, and it shouldn't depend on how wide the window is. */
    @media ${bp.wide} {
        padding: 56px 32px 24px;
    }
`;

export const MsFooter = styled.div<MsBodyProps>`
    position: relative;
    z-index: 3;
    padding:
        14px
        calc(22px + var(--safe-right))
        calc(22px + var(--safe-bottom))
        calc(22px + var(--safe-left));
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    max-width: ${p => p.wide ? "var(--content-max-wide)" : "var(--content-max)"};
    margin: 0 auto;

    @media ${bp.wide} {
        padding: 12px 32px 56px;
    }
`;

/**
 * Full-paper sheet (Settings, Roster) that stands in *for* a screen rather than
 * floating above it. Callers render a sheet instead of the screen beneath, so
 * this wrapper stays transparent and in-flow and the one global background —
 * paper, grain, vignette and the shared PageBack word field, all fixed at
 * z-index 0 — shows through untouched.
 *
 * A sheet must never paint its own paper: an opaque fill occludes the grain and
 * vignette as well as the word field, which then has to be clawed back with a
 * second PageBack canvas that drifts out of step with the first.
 */
export const MsSheet = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    /* Stands in for a screen, so it's bounded like one — without this the body
     * inside it has no height to be given and collapses to nothing. clip for
     * the same reason as MsScreen: never hand iOS a dead scroll container. */
    min-height: 0;
    overflow: clip;
    animation: ms-fade-up 0.32s ${easing.standard} both;
`;

/* ============================================================
   TYPE — display, deck, eyebrow
   ============================================================ */

export const MsDisplay = styled.div<{ italic?: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-style: ${p => p.italic ? "italic" : "normal"};
    letter-spacing: ${p => p.italic ? "-0.012em" : "-0.018em"};
    line-height: 1.0;
    color: ${paper.ink};
    text-wrap: balance;
`;

export const MsDeck = styled.p`
    font-family: ${fonts.serif};
    font-weight: 400;
    font-style: italic;
    font-size: 16px;
    line-height: 1.4;
    color: ${paper.inkSoft};
    text-wrap: pretty;
    margin: 0;
`;

export type MsEyebrowTone = "soft" | "mute" | "red" | "sticky";

const eyebrowTone: Record<MsEyebrowTone, string> = {
    soft:   paper.inkSoft,
    mute:   paper.inkMute,
    red:    paper.red,
    sticky: paper.sticky,
};

/**
 * Typewriter eyebrow — the small uppercase label that sits above a headline.
 *
 * `tone` names the four the design actually uses: `soft` (default), `mute` for
 * secondary/awaiting copy, `red` for accent, and `sticky` for a label sitting on
 * an ink-dark surface (champion plate, round leader). It replaces the old `red`
 * boolean, which only covered one of them — so eight files had each declared
 * their own `styled(MsEyebrow)` purely to set inkMute.
 */
export const MsEyebrow = styled.div<{ tone?: MsEyebrowTone }>`
    font-family: ${fonts.sans};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${p => eyebrowTone[p.tone ?? "soft"]};
`;

/* ============================================================
   SPINNER
   ============================================================ */

const spin = keyframes`to { transform: rotate(360deg); }`;

/** Ring spinner for a hero-scale wait — validating a tag, joining a room.
 *  Notice keeps its own smaller one; that one is coloured by notice tone. */
export const MsSpinner = styled.div`
    width: 48px;
    height: 48px;
    border: 3px solid ${shade(0.18)};
    border-top-color: ${paper.red};
    border-radius: 50%;
    animation: ${spin} 1s ${easing.soft} infinite;
    ${calmLoop}
`;

/* ============================================================
   FIELD ERROR — red-ink slip
   ============================================================ */

/**
 * Inline error slip — a red-ink note that sits in flow next to whatever it's
 * about, rather than floating over the page like a toast. A motion.div so
 * callers can animate it inside an AnimatePresence.
 */
export const MsFieldError = styled(motion.div)`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${fonts.sans};
    font-size: 13px;
    font-weight: 600;
    color: ${paper.redDeep};
    background: ${redA(0.08)};
    border: 1px solid ${redA(0.28)};
    border-radius: 10px;
    padding: 10px 12px;
    text-align: left;
`;

/* ============================================================
   RULES / DIVIDERS
   ============================================================ */

export const MsRule = styled.div`
    display: block;
    height: 1px;
    background: ${paper.rule};
    width: 100%;
    opacity: 0.6;
`;

export const MsRuleDouble = styled.div`
    height: 4px;
    border-top: 1px solid ${paper.rule};
    border-bottom: 1px solid ${paper.rule};
    opacity: 0.6;
`;

export const MsRuleRed = styled.div`
    display: block;
    height: 3px;
    background: ${paper.red};
    width: 32px;
    border-radius: 2px;
`;

/* ============================================================
   PAPER CARD — base paper element with deckle/shadow
   ============================================================ */

/**
 * Deckle effect: the card's paperCool background is rendered on a
 * pseudo-element whose edges are warped per-pixel by an SVG
 * `feDisplacementMap` filter (defs live in index.html). The filter
 * operates in pixel space, so the wobble amplitude and frequency are
 * uniform regardless of the card's aspect ratio — long edges no longer
 * stretch the effect — and the displaced silhouette is naturally
 * smooth-curved rather than the previous straight-line polygon segments.
 *
 * The card element itself stays a clean rectangle with `background:
 * transparent` and `box-shadow: none`, plus a soft drop-shadow filter
 * that casts off the rendered silhouette (the torn paper + any tape
 * strips), so overflow children like `MsTape` aren't clipped and pick
 * up their own small shadows naturally.
 *
 * Three filter variants (#ms-deckle-1/2/3) cycle via `:nth-of-type` so
 * adjacent cards don't share an identical edge.
 */
const deckleEdge = css`
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    isolation: isolate;

    filter:
        drop-shadow(0 1px 1px rgba(60,45,20,0.22))
        drop-shadow(0 4px 6px rgba(60,45,20,0.18))
        drop-shadow(0 16px 22px rgba(60,45,20,0.22));

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${paper.paperCool};
        filter: url(#ms-deckle-1);
        z-index: -1;
        pointer-events: none;
    }

    &:nth-of-type(3n+2)::after { filter: url(#ms-deckle-2); }
    &:nth-of-type(3n+3)::after { filter: url(#ms-deckle-3); }
`;

interface MsPaperProps {
    deckle?: boolean;
    tiltLeft?: boolean;
    tiltRight?: boolean;
    drop?: boolean;
    r0?: string;
    r1?: string;
}

export const MsPaper = styled.div<MsPaperProps>`
    position: relative;
    background: ${paper.paperCool};
    border-radius: 4px;
    padding: 22px;
    box-shadow:
        0 1px 0 rgba(255,255,255,0.65) inset,
        0 0 0 1px rgba(60,45,20,0.06),
        0 1px 1px rgba(60,45,20,0.04),
        0 12px 24px -14px rgba(60,45,20,0.35),
        0 24px 38px -22px rgba(60,45,20,0.45);

    --r0: ${p => p.r0 ?? "0deg"};
    --r1: ${p => p.r1 ?? "0deg"};

    ${p => p.tiltLeft && css`transform: rotate(-1.2deg);`}
    ${p => p.tiltRight && css`transform: rotate(1.2deg);`}
    ${p => p.r1 && !p.drop && css`transform: rotate(${p.r1});`}

    ${p => p.deckle && deckleEdge}

    ${p => p.drop && css`
        animation: ms-paper-drop 0.7s ${easing.pop} both;
    `}
`;

/* Index card — ruled with red top-margin line + hole punch */
export const MsIndex = styled.div<{ drop?: boolean; r1?: string }>`
    position: relative;
    background: ${paper.paperCool};
    border-radius: 6px;
    padding: 30px 22px 22px;
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.08),
        0 14px 28px -14px rgba(60,45,20,0.4),
        0 30px 50px -28px rgba(60,45,20,0.5);
    background-image:
        linear-gradient(transparent calc(24px - 1px), rgba(200, 57, 44, 0.5) 24px, rgba(200, 57, 44, 0.5) 25px, transparent 25px),
        repeating-linear-gradient(to bottom, transparent 0, transparent 26px, rgba(60, 90, 130, 0.18) 26px, rgba(60, 90, 130, 0.18) 27px, transparent 27px);
    background-position: 0 0, 0 36px;
    background-size: 100% 100%, 100% 100%;
    background-repeat: no-repeat, repeat;

    --r1: ${p => p.r1 ?? "0deg"};

    &::before {
        content: '';
        position: absolute;
        left: 18px; top: 14px;
        width: 7px; height: 7px;
        border-radius: 50%;
        background: rgba(60,45,20,0.18);
        box-shadow: inset 0 1px 1.5px rgba(0,0,0,0.35);
    }

    ${p => p.drop && css`
        animation: ms-paper-drop 0.7s ${easing.pop} both;
    `}
`;

/* ============================================================
   HEADLINE PLATE — black banner card
   ============================================================ */

export const MsPlate = styled.div<{ drop?: boolean; r1?: string }>`
    position: relative;
    background: ${paper.ink};
    color: ${paper.paper};
    border-radius: 8px;
    padding: 26px 22px 22px;
    box-shadow:
        0 0 0 1px rgba(0,0,0,0.4) inset,
        0 1px 0 rgba(255,255,255,0.06) inset,
        0 18px 36px -16px rgba(0,0,0,0.5),
        0 36px 50px -28px rgba(0,0,0,0.55);

    --r1: ${p => p.r1 ?? "0deg"};
    ${p => p.r1 && !p.drop && css`transform: rotate(${p.r1});`}

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.0' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.06 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        mix-blend-mode: screen;
        opacity: 0.7;
    }

    ${p => p.drop && css`
        animation: ms-paper-drop 0.7s ${easing.pop} both;
    `}
`;

/* ============================================================
   RECEIPT — perforated edge strip
   ============================================================ */

export const MsReceipt = styled.div`
    position: relative;
    background: ${paper.paperCool};
    padding: 14px 18px;
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.06),
        0 6px 14px -8px rgba(60,45,20,0.3);

    &::before, &::after {
        content: '';
        position: absolute;
        left: 0; right: 0;
        height: 8px;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='8'><path d='M0,0 L4,8 L8,0 L12,8 L16,0' fill='${encodeURIComponent(paper.paper)}'/></svg>");
        background-size: 16px 8px;
    }
    &::before { top: -1px; transform: scaleY(-1); }
    &::after  { bottom: -1px; }
`;

/* ============================================================
   TAPE STRIPS
   ============================================================ */

interface MsTapeProps {
    placement?: "tl" | "tr" | "bl";
    red?: boolean;
}

export const MsTape = styled.span<MsTapeProps>`
    position: absolute;
    width: 64px;
    height: 18px;
    background: ${p => p.red ? paper.tapeRed : paper.tape};
    box-shadow: 0 2px 4px rgba(60,45,20,0.18);
    pointer-events: none;
    z-index: 4;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: repeating-linear-gradient(135deg,
            transparent 0, transparent 4px,
            rgba(255,255,255,0.18) 4px, rgba(255,255,255,0.18) 5px);
    }

    ${p => {
        switch (p.placement) {
            case "tr": return css`top: -8px; right: 18px; transform: rotate(8deg);`;
            case "bl": return css`bottom: -8px; left: 22px; transform: rotate(5deg);`;
            case "tl":
            default:   return css`top: -8px; left: 18px; transform: rotate(-6deg);`;
        }
    }}
`;

/* ============================================================
   STICKY NOTE
   ============================================================ */

export const MsSticky = styled.span`
    position: relative;
    display: inline-block;
    background: linear-gradient(165deg, #f5e58a 0%, #efd76d 100%);
    padding: 10px 12px;
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 14px;
    color: ${paper.ink};
    box-shadow:
        0 1px 0 rgba(255,255,255,0.5) inset,
        0 1px 1px rgba(60,45,20,0.1),
        2px 6px 12px -4px rgba(60,45,20,0.35);
    transform: rotate(-2deg);
`;

/* ============================================================
   CHIP — soft paper pill, e.g. "awaiting player" name tags
   ============================================================ */

/**
 * Small serif-italic pill used to list players we're waiting on. A
 * motion.span so callers can drive layout / enter-exit animation inside an
 * AnimatePresence list.
 *
 * `size="sm"` is the same pill for places where the list is ambient rather than
 * the subject of the screen — under the voting grid, say, where it sits beneath
 * the entries and must not compete with them. Same shape, same paper, one step
 * quieter; a screen that lists awaiting players should never invent its own.
 */
export const MsChip = styled(motion.span, {
    shouldForwardProp: (prop: string) => prop !== "size",
})<{ size?: "sm" | "md" }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: ${p => p.size === "sm" ? "4px 10px" : "6px 12px"};
    border-radius: 999px;
    background: ${paper.paperCool};
    color: ${p => p.size === "sm" ? paper.inkSoft : paper.ink};
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.08),
        0 4px 10px -6px rgba(60,45,20,0.25);
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: ${p => p.size === "sm" ? "12px" : "14px"};
`;

/* ============================================================
   STAMP — red ink, rotated
   ============================================================ */

export type MsStampTone = "red" | "ink";

const stampTone: Record<MsStampTone, string> = {
    red: paper.red,
    ink: paper.sticky,
};

/**
 * Rubber stamp — rotated, hard-bordered, ink-on-paper.
 *
 * Two sizes, because the design genuinely uses the gesture at two scales. `md`
 * (default) is the headline stamp: "Champion", "No winner". `sm` is the marker
 * pressed into a dense list — the roster's "Removed" / "Hands on" — and is
 * tighter on every axis rather than a scaled-down copy, since 2.5px ink and
 * 0.06em tracking go muddy at that size.
 *
 * `sm` also skips the noise overlay: the texture reads as grain at headline
 * scale but as dirt at 10px, and it's the one thing that can't just shrink.
 */
const stampConfig = {
    shouldForwardProp: (prop: string) => !["tone", "size"].includes(prop),
};

export const MsStamp = styled("span", stampConfig)<{ size?: "sm" | "md"; tone?: MsStampTone }>`
    /* The gesture itself, shared by both scales. */
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.sans};
    font-weight: 800;
    text-transform: uppercase;
    position: relative;
    color: ${p => stampTone[p.tone ?? "red"]};
    border-style: solid;
    border-color: ${p => stampTone[p.tone ?? "red"]};

    ${p => p.size === "sm"
        ? css`
            /* Marker pressed into a dense list. Tighter on every axis rather
               than scaled down, and no noise — see the note above. The fill is
               picked to hold the border against a roster row that may be
               ink-dark (you) or paper (everyone else). */
            flex-direction: row;
            border-width: 2px;
            border-radius: 3px;
            padding: 4px 8px;
            font-size: 10px;
            letter-spacing: 0.08em;
            transform: rotate(-4deg);
            background: ${p.tone === "ink" ? shade(0.4) : paperA(0.6)};
        `
        : css`
            flex-direction: column;
            border-width: 2.5px;
            border-radius: 4px;
            padding: 8px 16px;
            font-size: 11px;
            letter-spacing: 0.06em;
            transform: rotate(-6deg);
            background: ${p.tone === "ink" ? stickyA(0.1) : redA(0.04)};

            &::before, &::after {
                content: '';
                position: absolute;
                background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0'/><feComposite in='SourceGraphic' operator='in'/></filter><rect width='100%' height='100%' filter='url(%23n)' fill='white' opacity='0.65'/></svg>");
                inset: 0;
                pointer-events: none;
                mix-blend-mode: lighten;
                opacity: 0.5;
            }
        `}
`;

/* ============================================================
   BUTTONS — paper press buttons (tactile, not flat)
   ============================================================ */

export type MsBtnVariant = "default" | "red" | "ink" | "ghost";

interface MsBtnProps extends Omit<HTMLMotionProps, "color"> {
    variant?: MsBtnVariant;
    fullWidth?: boolean;
}

type HTMLMotionProps = ButtonHTMLAttributes<HTMLButtonElement>;

const btnBase = css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    border-radius: 14px;
    border: 0;
    font-family: ${fonts.sans};
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.005em;
    transition: transform 0.16s ${easing.pop}, box-shadow 0.16s ${easing.standard};
    cursor: pointer;

    &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const btnDefault = css`
    background: ${paper.paperCool};
    color: ${paper.ink};
    box-shadow:
        0 1px 0 rgba(255,255,255,0.7) inset,
        0 -1px 0 rgba(60,45,20,0.06) inset,
        0 2px 0 rgba(60,45,20,0.18),
        0 4px 12px -4px rgba(60,45,20,0.18);
    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow:
            0 1px 0 rgba(255,255,255,0.7) inset,
            0 -1px 0 rgba(60,45,20,0.06) inset,
            0 3px 0 rgba(60,45,20,0.22),
            0 8px 18px -4px rgba(60,45,20,0.25);
    }
    &:active:not(:disabled) {
        transform: translateY(1px);
        box-shadow:
            0 1px 0 rgba(255,255,255,0.7) inset,
            0 -1px 0 rgba(60,45,20,0.06) inset,
            0 1px 0 rgba(60,45,20,0.18),
            0 2px 6px -2px rgba(60,45,20,0.25);
    }
`;

const btnRed = css`
    background: linear-gradient(180deg, ${paper.redLight} 0%, ${paper.red} 100%);
    color: ${paper.paper};
    box-shadow:
        0 1px 0 rgba(255,255,255,0.18) inset,
        0 -1px 0 rgba(0,0,0,0.18) inset,
        0 3px 0 ${paper.redDeep},
        0 6px 14px -4px rgba(60,45,20,0.35);
    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow:
            0 1px 0 rgba(255,255,255,0.18) inset,
            0 -1px 0 rgba(0,0,0,0.18) inset,
            0 4px 0 ${paper.redDeep},
            0 12px 22px -4px rgba(60,45,20,0.4);
    }
    &:active:not(:disabled) {
        transform: translateY(1px);
        box-shadow:
            0 1px 0 rgba(255,255,255,0.18) inset,
            0 -1px 0 rgba(0,0,0,0.18) inset,
            0 1px 0 ${paper.redDeep},
            0 3px 8px -2px rgba(60,45,20,0.4);
    }
`;

const btnInk = css`
    background: linear-gradient(180deg, #2a251e 0%, ${paper.ink} 100%);
    color: ${paper.paper};
    box-shadow:
        0 1px 0 rgba(255,255,255,0.06) inset,
        0 -1px 0 rgba(0,0,0,0.5) inset,
        0 3px 0 ${paper.inkDeep},
        0 6px 14px -4px rgba(0,0,0,0.5);
    &:hover:not(:disabled) {
        transform: translateY(-1px);
    }
    &:active:not(:disabled) {
        transform: translateY(1px);
    }
`;

const btnGhost = css`
    background: transparent;
    color: ${paper.inkSoft};
    box-shadow: 0 0 0 1.5px rgba(29,26,20,0.18);
    &:hover:not(:disabled) {
        background: rgba(29,26,20,0.04);
        box-shadow: 0 0 0 1.5px rgba(29,26,20,0.32);
    }
`;

const BtnFace = styled.button<MsBtnProps>`
    ${btnBase}
    ${p => {
        switch (p.variant) {
            case "red":   return btnRed;
            case "ink":   return btnInk;
            case "ghost": return btnGhost;
            default:      return btnDefault;
        }
    }}
    ${p => p.fullWidth !== false && css`width: 100%;`}
`;

/**
 * Default haptic per variant, which is possible only because the variants mean
 * something: `red` is always the screen's primary move, so it gets the firm
 * thud; `ghost` is always the way out or the subordinate option, so it gets the
 * lightest mark. A screen that wants a destructive confirm to feel destructive
 * passes `haptic="destructive"` — it's the one case the variant can't tell.
 */
const BTN_HAPTIC: Record<MsBtnVariant, HapticName> = {
    red:     "submit",
    default: "select",
    ink:     "select",
    ghost:   "tap",
};

export const MsBtn = hapticControl<MsBtnProps>(
    BtnFace,
    p => BTN_HAPTIC[p.variant ?? "default"],
    "MsBtn",
);

/**
 * The blocked state: what stands in the footer when the next move is somebody
 * else's. It occupies the slot the real button would, in the same shape, so a
 * screen doesn't reflow the moment you stop being the one holding the pen.
 *
 * Always name the person. Half these screens are gated on the room host and half
 * on whoever holds the round — dealer, presenter — and to the player waiting
 * that distinction is noise; the name is the useful part. Which is why this takes
 * a `name` and not a flag: "Waiting for host" is a bug, not a label.
 */
export function MsWaitingFor({ name }: { name: string }) {
    return (
        <MsBtn variant="ghost" disabled>
            Waiting for {name} <Dots />
        </MsBtn>
    );
}

/* Pill icon button used in lobby for share/QR */
const IconBtnFace = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: ${paper.paperCool};
    border-radius: 999px;
    font-family: ${fonts.sans};
    font-weight: 600;
    font-size: 13px;
    color: ${paper.ink};
    border: 0;
    box-shadow:
        0 1px 0 rgba(255,255,255,0.6) inset,
        0 0 0 1px rgba(60,45,20,0.06),
        0 4px 10px -4px rgba(60,45,20,0.3);
    transition: transform 0.15s ${easing.pop};

    &:hover { transform: translateY(-1px); }
`;

export const MsIconBtn = hapticControl(IconBtnFace, "tap", "MsIconBtn");

/* ============================================================
   TOGGLE — paper switch
   ============================================================ */

interface PaperToggleProps {
    on: boolean;
    onChange: (next: boolean) => void;
    /** A control the viewer may look at but not change. Now that the switch
     *  buzzes, an inert-but-live one would feel like it took the change and
     *  then didn't — so read-only callers say so here rather than swallowing
     *  the change in their handler. */
    disabled?: boolean;
    "aria-label"?: string;
}

/* `on` is ours, but it's also a valid DOM attribute name, so emotion's default
 * prop filter happily forwards it and React warns about a non-boolean `on`. */
const toggleConfig = {
    shouldForwardProp: (prop: string) => prop !== "on",
};

const ToggleTrack = styled("button", toggleConfig)<{ on: boolean }>`
    position: relative;
    width: 52px;
    height: 28px;
    border-radius: 999px;
    border: 0;
    padding: 0;
    cursor: pointer;
    background: ${p => p.on ? paper.ink : paper.paperDeep};
    box-shadow: ${p => p.on
        ? `0 1px 0 rgba(255,255,255,0.06) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 2px 0 ${paper.inkDeep}`
        : "0 1px 0 rgba(255,255,255,0.5) inset, 0 0 0 1px rgba(60,45,20,0.18), 0 2px 0 rgba(60,45,20,0.18)"};
    transition: background 0.22s ${easing.standard};
`;

const ToggleKnob = styled("span", toggleConfig)<{ on: boolean }>`
    position: absolute;
    top: 2.5px;
    left: ${p => p.on ? 26 : 3}px;
    width: 23px;
    height: 23px;
    border-radius: 50%;
    background: ${paper.paperCool};
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.16),
        0 1px 0 rgba(255,255,255,0.65) inset,
        0 2px 4px rgba(60,45,20,0.3);
    transition: left 0.22s ${easing.pop};
`;

/**
 * Paper switch.
 *
 * `role="switch"` + `aria-checked` is what makes this announce as "on"/"off"
 * rather than as an unlabelled button whose state is carried purely by where the
 * knob happens to sit.
 */
export function PaperToggle({ on, onChange, disabled, ...rest }: PaperToggleProps) {
    return (
        <ToggleTrack
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled}
            on={on}
            onClick={() => { haptics.tap(); onChange(!on); }}
            {...rest}
        >
            <ToggleKnob on={on} />
        </ToggleTrack>
    );
}

/* ============================================================
   CHIPS — segmented control (paper)
   ============================================================ */

interface PaperChipsProps<T> {
    options: T[];
    labels?: (string | number)[];
    value: T;
    onChange: (next: T) => void;
    /** See PaperToggle's `disabled` — same reasoning. */
    disabled?: boolean;
    /** `md` (default) is the settings-sheet control, where the chips *are* the
     *  content. `sm` is for a control that sits beside something it mustn't
     *  outweigh — the pool's order switch, next to a line of source type. */
    size?: "sm" | "md";
    suffix?: string;
}

const ChipsOuter = styled.div`
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
`;

const chipConfig = {
    shouldForwardProp: (prop: string) => prop !== "active" && prop !== "size",
};

const Chip = styled("button", chipConfig)<{ active: boolean; size?: "sm" | "md" }>`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: ${p => p.size === "sm" ? "13px" : "17px"};
    padding: ${p => p.size === "sm" ? "5px 10px" : "8px 14px"};
    border-radius: 4px;
    border: 0;
    cursor: pointer;
    background: ${p => p.active ? paper.ink : paper.paperCool};
    color: ${p => p.active ? paper.paper : paper.ink};
    box-shadow: ${p => p.active
        ? `0 0 0 1px ${paper.ink}, 0 1px 0 rgba(255,255,255,0.06) inset, 0 2px 0 ${paper.inkDeep}, 0 4px 10px -4px rgba(0,0,0,0.35)`
        : "0 0 0 1px rgba(60,45,20,0.12), 0 1px 0 rgba(255,255,255,0.55) inset, 0 2px 0 rgba(60,45,20,0.14)"};
    transform: ${p => p.active ? "translateY(-1px) rotate(-0.6deg)" : "rotate(0)"};
    transition: transform 0.16s ${easing.pop}, box-shadow 0.16s, background 0.16s;
    font-variant-numeric: tabular-nums;
`;

const ChipSuffix = styled.span<{ active: boolean }>`
    font-weight: 500;
    font-style: italic;
    margin-left: 2px;
    opacity: ${p => p.active ? 0.7 : 0.55};
`;

export function PaperChips<T extends string | number | null>({
    options, labels, value, onChange, disabled, size, suffix,
}: PaperChipsProps<T>) {
    return (
        <ChipsOuter>
            {options.map((o, i) => {
                const active = o === value;
                const label = labels?.[i] ?? (o === null ? "∞" : String(o));
                return (
                    <Chip
                        key={String(o ?? `null-${i}`)}
                        active={active}
                        size={size}
                        disabled={disabled}
                        // Re-pressing the chip you're already on changes nothing,
                        // so it shouldn't feel like it did.
                        onClick={() => { if (!active) haptics.select(); onChange(o); }}
                    >
                        {label}{suffix && <ChipSuffix active={active}>{suffix}</ChipSuffix>}
                    </Chip>
                );
            })}
        </ChipsOuter>
    );
}

export const MsFieldLabel = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 8px;

    em {
        font-family: ${fonts.serif};
        font-style: italic;
        font-size: 12px;
        color: ${paper.inkMute};
    }
`;

/**
 * Circular icon button.
 *
 * The app presses three of these — the back chevron, the host cog, and a notice
 * dismiss — and they had each been declared from scratch at 36 / 38 / 24px. The
 * geometry and the press (lift on hover, sink on active) are the same gesture in
 * all three; only the substrate differs, which is what `tone` names:
 *
 *   `paper` — paper-tone press on paper. Lifts, and warms toward ink.
 *   `ink`   — the inverse: ink disc on paper, for a control that must be found
 *             over any screen (the cog floats above every phase).
 *   `ghost`  — no disc at all, just a wash that deepens. For a dismiss that
 *             should stay subordinate to the thing it closes.
 *
 * `size` is plain pixels because the three are not a scale — each is sized to
 * its context, not to a step on a ramp.
 */
export type MsCircleBtnTone = "paper" | "ink" | "ghost";

const circleBtnConfig = {
    shouldForwardProp: (prop: string) => !["tone", "size"].includes(prop),
};

const CircleBtnFace = styled("button", circleBtnConfig)<{ tone?: MsCircleBtnTone; size?: number }>`
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0;
    width: ${p => p.size ?? 36}px;
    height: ${p => p.size ?? 36}px;

    ${p => {
        switch (p.tone ?? "paper") {
            case "ink":
                return css`
                    background: ${paper.ink};
                    color: ${paper.paper};
                    box-shadow:
                        0 0 0 1px ${paper.ink} inset,
                        0 2px 0 ${paper.inkDeep},
                        0 6px 14px -4px ${blackA(0.4)};
                    transition: transform 0.16s ${easing.pop};

                    &:hover { transform: translateY(-1px); }
                    &:active { transform: translateY(1px); }
                `;
            case "ghost":
                return css`
                    background: ${shade(0.06)};
                    color: ${paper.inkSoft};

                    &:hover {
                        background: ${shade(0.12)};
                        color: ${paper.ink};
                    }
                `;
            default:
                return css`
                    background: ${paper.paperCool};
                    color: ${paper.inkSoft};
                    box-shadow:
                        0 1px 0 ${gloss(0.7)} inset,
                        0 0 0 1px ${shade(0.08)},
                        0 2px 0 ${shade(0.14)},
                        0 5px 12px -4px ${shade(0.3)};
                    transition:
                        transform 0.16s ${easing.pop},
                        color 0.16s,
                        box-shadow 0.16s ${easing.standard};

                    &:hover {
                        color: ${paper.ink};
                        transform: translateY(-1px);
                    }
                    &:active {
                        transform: translateY(1px);
                        box-shadow:
                            0 1px 0 ${gloss(0.7)} inset,
                            0 0 0 1px ${shade(0.08)},
                            0 1px 0 ${shade(0.14)},
                            0 2px 6px -2px ${shade(0.3)};
                    }
                `;
        }
    }}
`;

export const MsCircleBtn = hapticControl<Pressable & { tone?: MsCircleBtnTone; size?: number }>(
    CircleBtnFace,
    "tap",
    "MsCircleBtn",
);

/* Back button — the paper circle press, plus an inset arrow that slides left on
 * hover. Sits at the start of a column, so it opts out of stretch. */
const BackBtn = styled(MsCircleBtn)`
    align-self: flex-start;

    & > svg { transition: transform 0.18s ${easing.pop}; }
    &:hover > svg { transform: translateX(-2px); }
`;

interface MsBackButtonProps {
    onClick: () => void;
    /** Accessible label — defaults to "Back". Pass e.g. "Roster" on Player
     *  Manage screens so screen readers know where the button leads. */
    label?: string;
    className?: string;
}

export function MsBackButton({ onClick, label = "Back", className }: MsBackButtonProps) {
    return (
        <BackBtn type="button" onClick={onClick} aria-label={label} className={className}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M15 6l-6 6 6 6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </BackBtn>
    );
}

/* ============================================================
   TILE — word chip in answer builder
   ============================================================ */

/**
 * The tile's face, shared by the pressable tile and the static card below.
 *
 * Kept as a css block because the same paper chip has to sit on two different
 * elements: `styled(MsTile)` + `as="div"` used to do that job, and stopped
 * working the moment MsTile became a component — `as` swaps the rendered element
 * and skips the wrapper entirely.
 */
const msTileFace = css`
    display: inline-flex;
    align-items: center;
    padding: 7px 11px;
    background: ${paper.paperCool};
    border-radius: 4px;
    border: 0;
    font-family: ${fonts.serif};
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.005em;
    color: ${paper.ink};
    cursor: pointer;
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.08),
        0 2px 4px -2px rgba(60,45,20,0.18),
        0 6px 10px -6px rgba(60,45,20,0.25);
    transition: transform 0.16s ${easing.pop}, box-shadow 0.16s;
    animation: ms-fade-up 0.4s ${easing.standard} both;

    &:hover:not(:disabled) {
        transform: translateY(-2px) rotate(-0.5deg);
        box-shadow:
            0 0 0 1px rgba(60,45,20,0.16),
            0 4px 8px -2px rgba(60,45,20,0.25),
            0 12px 20px -8px rgba(60,45,20,0.35);
    }
`;

const wordConfig = {
    shouldForwardProp: (prop: string) => prop !== "used",
};

/**
 * The red-ink "this one's in your answer" mark.
 *
 * Shared by both word surfaces — the chip and the prose column — so a word reads
 * the same whichever way the pool is laid out. It marks rather than spends: the
 * pool is a passage from the article, not a hand of cards, so a used word stays
 * fully live and fully legible.
 *
 * Marked, and no more than that. How many times you've played a word is already
 * in front of you in the answer strip, and a tally on the page only competed
 * with it.
 */
const usedWordMark = css`
    text-decoration: underline;
    text-decoration-color: ${redA(0.5)};
    text-decoration-thickness: 1.5px;
    text-underline-offset: 3px;
`;

const TileFace = styled("button", wordConfig)<{ used?: boolean }>`
    ${msTileFace}

    ${p => p.used && css`
        background: ${redA(0.07)};
        box-shadow:
            0 0 0 1px ${redA(0.3)},
            0 2px 4px -2px ${shade(0.18)},
            0 6px 10px -6px ${shade(0.25)};
        ${usedWordMark}
    `}
`;

/* `tap`, not `select`: a word tile is picked up dozens of times a round, and the
 * heavier pip turns a fast build into a rattle. Screens where a tile *is* the
 * commit (choosing your article) pass `haptic="select"`. */
export const MsTile = hapticControl<Pressable & { used?: boolean }>(TileFace, "tap", "MsTile");

/** The same chip with nothing to press — a card that belongs to the tile family
 *  rather than a button pretending not to be one. No haptic, by definition. */
export const MsTileCard = styled.div`
    ${msTileFace}
    cursor: default;
`;

/**
 * A word set as type rather than as a chip — the pool laid out as a column of
 * prose instead of a tray of counters.
 *
 * Which is what it honestly is: the article with every repeat removed, so the
 * opening reads like the source and then dissolves into it. Setting it as text
 * says that; a grid of raised cards says "controls", and buries a couple of
 * thousand words in padding and gaps besides — prose fits far more of them on a
 * screen, which is the difference between scanning the pool and scrolling it.
 *
 * `inline-block` rather than `inline` is what makes it playable: vertical
 * padding on an inline box doesn't move the lines around it, so the hit area
 * would silently overlap its neighbours. As a block the padding is real, and the
 * column is set with more air between lines than a printed page would use — a
 * page is for reading, this is for hitting with a thumb.
 */
const ProseWordFace = styled("button", wordConfig)<{ used?: boolean }>`
    display: inline-block;
    /* Asymmetric on purpose. The vertical padding is what separates one row of
     * targets from the next, so it stays generous; the horizontal is doubled up
     * between neighbours and then stretched again by justification, so a little
     * goes a long way and too much reads as a word list rather than a line. */
    padding: 7px 4px;
    margin: 3px 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    font-family: ${fonts.serif};
    font-weight: 500;
    font-size: 17px;
    line-height: 1.25;
    letter-spacing: -0.004em;
    color: ${paper.ink};
    cursor: pointer;
    /* A long chemical name is better broken than sent through the right margin. */
    max-width: 100%;
    overflow-wrap: break-word;
    transition: background 0.14s ${easing.standard}, color 0.14s;

    &:hover {
        background: ${inkA(0.06)};
    }

    &:active {
        background: ${redA(0.12)};
        color: ${paper.redDeep};
    }

    ${p => p.used && css`
        background: ${redA(0.1)};
        color: ${paper.redDeep};
        ${usedWordMark}
    `}
`;

export const MsProseWord = hapticControl<Pressable & { used?: boolean }>(
    ProseWordFace,
    "tap",
    "MsProseWord",
);

/* ============================================================
   COMPOSE STRIP — torn newspaper headline (builder output)
   ============================================================ */

export const MsCompose = styled.div`
    position: relative;
    min-height: 96px;
    padding: 18px 18px 22px;
    background: ${paper.paperCool};
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 8px;
    align-items: baseline;
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.06),
        0 12px 24px -16px rgba(60,45,20,0.4);
`;

export const MsComposePlaceholder = styled.div`
    position: absolute;
    top: 50%;
    left: 18px;
    right: 18px;
    transform: translateY(-50%);
    font-family: ${fonts.serif};
    font-style: italic;
    color: ${paper.inkMute};
    font-size: 17px;
    pointer-events: none;
    text-wrap: pretty;
`;

const ComposeWordFace = styled.button`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 22px;
    line-height: 1.05;
    color: ${paper.ink};
    letter-spacing: -0.012em;
    cursor: pointer;
    background: transparent;
    border: 0;
    padding: 0;
    animation: ms-tile-pop 0.32s ${easing.pop} both;
`;

/* Pairs with MsTile: taking a word back out of the headline is the same weight
 * of gesture as putting it in. */
export const MsComposeWord = hapticControl(ComposeWordFace, "tap", "MsComposeWord");

/* ============================================================
   QUOTE CARD — vote / reveal
   ============================================================ */

interface QuoteProps {
    voted?: boolean;
    disabled?: boolean;
}

const QuoteFace = styled.button<QuoteProps>`
    position: relative;
    width: 100%;
    text-align: left;
    background: ${paper.paperCool};
    color: ${paper.ink};
    border: 0;
    border-radius: 6px;
    padding: 22px 20px;
    box-shadow:
        0 0 0 1px rgba(60,45,20,0.06),
        0 12px 24px -14px rgba(60,45,20,0.35),
        0 24px 36px -22px rgba(60,45,20,0.45);
    cursor: pointer;
    transition: transform 0.22s ${easing.pop}, box-shadow 0.22s;

    &:hover:not(:disabled) {
        transform: translateY(-3px) rotate(-0.4deg);
        box-shadow:
            0 0 0 1.5px rgba(60,45,20,0.2),
            0 18px 30px -14px rgba(60,45,20,0.4),
            0 28px 44px -20px rgba(60,45,20,0.5);
    }

    ${p => p.voted && css`
        background: ${paper.ink};
        color: ${paper.paper};
        transform: translateY(-3px) rotate(-0.4deg);
        box-shadow:
            0 0 0 1.5px ${paper.ink},
            0 18px 30px -14px rgba(0,0,0,0.45),
            0 28px 44px -20px rgba(0,0,0,0.55);
    `}

    ${p => p.disabled && css`opacity: 0.45; pointer-events: none;`}
`;

/* Pressing a quote card is casting a vote — the one soft commit the whole
 * screen exists for. */
export const MsQuote = hapticControl<Pressable & QuoteProps>(QuoteFace, "select", "MsQuote");

/**
 * The oversized quotation mark that sits *behind* a revealed entry — ink soaked
 * through the page rather than a glyph in the text. Position a `MsQuoteWrap`
 * around the headline and drop this in first.
 *
 * Distinct from MsQmark, which is a small solid mark inside a voting card. This
 * one is the reveal treatment, shared by the results and vote-results screens so
 * a winning entry looks like the same object it did when it was read out.
 */
export const MsQuoteWrap = styled.div`
    position: relative;
`;

export const MsBigQuote = styled.div`
    position: absolute;
    top: -22px;
    left: -10px;
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 130px;
    line-height: 0.7;
    color: ${paper.red};
    opacity: 0.2;
    pointer-events: none;
`;

export const MsQmark = styled.div<{ voted?: boolean }>`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 60px;
    line-height: 0.4;
    color: ${p => p.voted ? paper.sticky : paper.red};
    margin-bottom: 8px;
`;

export const MsQtext = styled.div`
    font-family: ${fonts.serif};
    font-weight: 500;
    font-style: italic;
    font-size: 22px;
    line-height: 1.22;
    letter-spacing: -0.005em;
    text-wrap: pretty;
`;

/* ============================================================
   PLAYER ROW
   ============================================================ */

const PlayerButtonFace = styled.button`
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    width: 100%;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
`;

/** Unstyled full-width button wrapper — makes a MsRow, an article card, or any
 *  content keyboard/click actionable without adding any visual chrome of its
 *  own. Carries `tap` so the content inside doesn't have to think about it;
 *  wrappers around a genuine choice pass `haptic="select"`. */
export const MsPlayerButton = hapticControl(PlayerButtonFace, "tap", "MsPlayerButton");

export const MsRow = styled.div<{ you?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px 12px 12px;
    background: ${p => p.you ? paper.ink : paper.paperCool};
    color: ${p => p.you ? paper.paper : paper.ink};
    border-radius: 12px;
    box-shadow:
        0 1px 0 rgba(255,255,255,${p => p.you ? "0.04" : "0.55"}) inset,
        0 0 0 1px rgba(60,45,20,${p => p.you ? "1" : "0.06"}),
        0 6px 14px -8px rgba(0,0,0,${p => p.you ? "0.4" : "0.3"});
    animation: ms-paper-drop 0.7s ${easing.pop} both;
`;

export const MsAvatar = styled.div<{ you?: boolean }>`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: ${p => p.you ? paper.sticky : paper.ink};
    color: ${p => p.you ? paper.ink : paper.paper};
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: -0.02em;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.25);
    text-transform: lowercase;
`;

export const MsName = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 18px;
    flex: 1;
    letter-spacing: -0.005em;
`;

export const MsNameMark = styled.span<{ you?: boolean }>`
    color: ${p => p.you ? paper.sticky : paper.red};
    margin-right: 4px;
    font-style: normal;
    font-weight: 800;
`;

/* ============================================================
   DOT LOADER + STATUS DOTS
   ============================================================ */

export const MsDots = styled.span`
    display: inline-flex;
    gap: 4px;
    align-items: center;

    span {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: ${paper.inkMute};
        animation: ms-dot 1.2s ease-in-out infinite;
        ${calmLoop}
    }
    span:nth-of-type(2) { animation-delay: 0.15s; }
    span:nth-of-type(3) { animation-delay: 0.3s; }
`;

export function Dots() {
    return <MsDots><span/><span/><span/></MsDots>;
}

/* ============================================================
   HEADLINE MASK REVEAL — split into words, animate each up
   ============================================================ */

function extractText(node: ReactNode): string {
    if (node == null || typeof node === "boolean") return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (isValidElement(node)) {
        // @ts-expect-error children may or may not be present
        return extractText(node.props && node.props.children);
    }
    return "";
}

const MaskSpan = styled.span`
    display: inline-block;
    overflow: hidden;
    vertical-align: bottom;
    margin-right: 0.25em;
    /* Extend the clip region just below the baseline so descenders (q, g, p, y, j)
       aren't sliced off. The negative margin cancels the extra height so the
       surrounding text layout is identical. */
    padding-bottom: 0.22em;
    margin-bottom: -0.22em;

    > span {
        display: inline-block;
        animation: ms-mask-up 0.7s ${easing.standard} both;
    }
`;

interface MaskHeadlineProps {
    children: ReactNode;
    delay?: number;
}

export function MaskHeadline({ children, delay = 0 }: MaskHeadlineProps) {
    const words = extractText(children).split(" ").filter(Boolean);
    return (
        <span>
            {words.map((w, i) => (
                <MaskSpan key={`${w}-${i}`}>
                    <span style={{ animationDelay: `${delay + i * 0.06}s` }}>{w}</span>
                </MaskSpan>
            ))}
        </span>
    );
}

/* ============================================================
   PAGE BACKDROP — depth-of-field field of drifting words
   ============================================================ */

/* Fewer words on phones: the field is the single biggest GPU cost (each word is
   an independently-animating, blurred, compositor-promoted layer), and phones
   have the least thermal/VRAM headroom. Desktop keeps the full density. */
const BACKDROP_COUNT =
    typeof window !== "undefined" && window.matchMedia?.(bp.phone).matches ? 16 : 30;
const FADE_DURATION = 3; // s — opacity in/out

interface Drift {
    radiusX: number;
    radiusY: number;
    phaseX: number;
    phaseY: number;
    rotAmp: number;
    rotPhase: number;
}

interface WordSprite {
    canvas: HTMLCanvasElement;
    w: number;          // css px draw width
    h: number;          // css px draw height
}

interface FloatingWord {
    text: string;
    x: number;          // vw %
    y: number;          // vh %
    size: number;       // px
    opacity: number;
    blur: number;       // px
    speed: number;      // s per orbit
    italic: boolean;
    weight: number;
    red: boolean;
    drift: Drift;
    lifetime: number;   // s
    createdAt: number;  // ms epoch — drives fade in/out + recycle
    /** Pre-rasterised bitmap (colour + blur baked in). Built lazily; nulled on
     *  DPR change / font load so it re-renders crisp. */
    sprite: WordSprite | null;
}

/* Depth (0 = far, 1 = near) drives a coherent parallax look: far words are
   small, faint, blurred and orbit slowly; near words are larger, sharper,
   bolder and orbit a touch faster. */
const makeWord = (): FloatingWord => {
    const depth = Math.random();
    const baseRadius = 12 + depth * 24;       // far orbits tight, near wider
    const red = Math.random() < 0.15;

    return {
        text: BACKDROP_WORDS[Math.floor(Math.random() * BACKDROP_WORDS.length)],
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 14 + depth * 28,                 // 14..42 px
        opacity: (0.04 + depth * 0.085) * (red ? 1.15 : 1),
        blur: 2.6 - depth * 2.6,               // far 2.6px → near crisp
        speed: 95 - depth * 48,                // far 95s → near 47s
        italic: Math.random() < 0.6,
        weight: Math.random() < 0.5 ? 700 : (Math.random() < 0.6 ? 400 : 800),
        red,
        drift: {
            radiusX: baseRadius * (0.6 + Math.random() * 0.6),
            radiusY: baseRadius * (0.6 + Math.random() * 0.6),
            phaseX: Math.random() * Math.PI * 2,
            phaseY: Math.random() * Math.PI * 2,
            rotAmp: 1.5 + Math.random() * 3,
            rotPhase: Math.random() * Math.PI * 2,
        },
        lifetime: 18 + Math.random() * 24,     // 18..42 s
        createdAt: Date.now(),
        sprite: null,
    };
};

const makeInitialWords = (): FloatingWord[] =>
    Array.from({ length: BACKDROP_COUNT }, () => {
        const w = makeWord();
        // Start mid-life so the field is already populated and de-synced.
        w.createdAt = Date.now() - Math.random() * w.lifetime * 1000;
        return w;
    });

const BackdropCanvas = styled.canvas`
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    user-select: none;
    z-index: 0;
`;

/**
 * Standardised page backdrop: a soft depth-of-field field of faint serif words
 * — the margin-vocabulary of doubtful quotation. Each word orbits gently on its
 * own Lissajous loop, near words sharp and bold, far words small and blurred,
 * and the whole field slowly recycles as words fade in and out.
 *
 * Rendered to a *single* `<canvas>` (one DOM node, one compositor layer) driven
 * by one rAF loop, rather than ~30 independently-animated, blurred, layer-
 * promoted DOM spans — that DOM approach pinned a lot of VRAM and ran the GPU
 * flat-out, which warmed phones. Each word is rasterised once (colour + blur
 * baked in) and then just blitted with a transform each frame, so the blur — the
 * priciest effect — costs nothing per frame. Mounted once at the app root.
 */
export function PageBack() {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        const TAU = Math.PI * 2;
        const FRAME_MS = 1000 / 30;          // 30fps — ample for a slow drift
        const words = makeInitialWords();
        const t0 = Date.now();

        const reduceMq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        let reduced = !!reduceMq?.matches;
        let dpr = 1;
        let raf = 0;
        let lastDraw = 0;

        const measure = document.createElement("canvas").getContext("2d");
        const fontOf = (w: FloatingWord) =>
            `${w.italic ? "italic " : ""}${w.weight} ${w.size}px ${fonts.serif}`;

        // Rasterise a word once, at device resolution, with colour + blur baked
        // in. Blitting this cached bitmap each frame is near-free.
        const rasterize = (w: FloatingWord): WordSprite => {
            let textW = w.size * 4;
            if (measure) { measure.font = fontOf(w); textW = measure.measureText(w.text).width; }
            const pad = Math.ceil(w.blur * 3) + 2;
            const cw = Math.ceil(textW) + pad * 2;
            const ch = Math.ceil(w.size * 1.4) + pad * 2;
            const c = document.createElement("canvas");
            c.width = Math.max(1, Math.round(cw * dpr));
            c.height = Math.max(1, Math.round(ch * dpr));
            const cx = c.getContext("2d")!;
            cx.scale(dpr, dpr);
            cx.font = fontOf(w);
            cx.textAlign = "center";
            cx.textBaseline = "middle";
            cx.fillStyle = w.red ? paper.red : paper.ink;
            if (w.blur > 0.05) cx.filter = `blur(${w.blur.toFixed(2)}px)`;
            cx.fillText(w.text, cw / 2, ch / 2);
            return { canvas: c, w: cw, h: ch };
        };

        const sizeCanvas = () => {
            dpr = window.devicePixelRatio || 1;
            canvas.width = Math.round(window.innerWidth * dpr);
            canvas.height = Math.round(window.innerHeight * dpr);
            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;
        };

        const draw = () => {
            const now = Date.now();
            const cssW = canvas.width / dpr;
            const cssH = canvas.height / dpr;
            const elapsed = reduced ? 0 : (now - t0) / 1000;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, cssW, cssH);

            for (let i = 0; i < words.length; i++) {
                let w = words[i];
                if (!reduced && (now - w.createdAt) / 1000 >= w.lifetime) {
                    w = words[i] = makeWord();
                }

                // Fade in over the first FADE_DURATION, out over the last.
                let f = 1;
                if (!reduced) {
                    const a = (now - w.createdAt) / 1000;
                    if (a < FADE_DURATION) f = a / FADE_DURATION;
                    else if (a > w.lifetime - FADE_DURATION) f = (w.lifetime - a) / FADE_DURATION;
                    f = f < 0 ? 0 : f > 1 ? 1 : f;
                }
                const alpha = w.opacity * f;
                if (alpha <= 0.002) continue;

                if (!w.sprite) w.sprite = rasterize(w);
                const sp = w.sprite;

                const ang = TAU * (elapsed / w.speed);
                const dx = w.drift.radiusX * Math.sin(ang + w.drift.phaseX);
                const dy = w.drift.radiusY * Math.sin(ang + w.drift.phaseY);
                const rot = w.drift.rotAmp * Math.sin(ang + w.drift.rotPhase) * (Math.PI / 180);
                const sc = 1 + 0.015 * Math.sin(ang);
                const px = (w.x / 100) * cssW + dx;
                const py = (w.y / 100) * cssH + dy;

                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                ctx.translate(px, py);
                ctx.rotate(rot);
                ctx.scale(sc, sc);
                ctx.globalAlpha = alpha;
                ctx.drawImage(sp.canvas, -sp.w / 2, -sp.h / 2, sp.w, sp.h);
            }
            ctx.globalAlpha = 1;
        };

        const loop = (ts: number) => {
            raf = requestAnimationFrame(loop);
            if (ts - lastDraw < FRAME_MS) return;     // throttle to ~30fps
            lastDraw = ts;
            draw();
        };
        const start = () => { if (!raf && !reduced) raf = requestAnimationFrame(loop); };
        const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } };

        // Don't burn frames behind a hidden tab; respect reduced-motion (draw a
        // single frozen frame and stop). Re-rasterise crisp once webfonts load.
        const onVisibility = () => {
            if (document.visibilityState === "hidden") stop();
            else { lastDraw = 0; start(); }
        };
        const onResize = () => {
            const prev = dpr;
            sizeCanvas();
            if (dpr !== prev) for (const w of words) w.sprite = null;
            draw();
        };
        const onReduceChange = () => {
            reduced = !!reduceMq?.matches;
            if (reduced) { stop(); draw(); } else { lastDraw = 0; start(); }
        };

        sizeCanvas();
        draw();                              // paint frame 0 immediately (no flash)
        if (!reduced && document.visibilityState !== "hidden") start();
        if (document.fonts?.ready) {
            document.fonts.ready.then(() => { for (const w of words) w.sprite = null; draw(); });
        }

        window.addEventListener("resize", onResize);
        document.addEventListener("visibilitychange", onVisibility);
        reduceMq?.addEventListener?.("change", onReduceChange);

        return () => {
            stop();
            window.removeEventListener("resize", onResize);
            document.removeEventListener("visibilitychange", onVisibility);
            reduceMq?.removeEventListener?.("change", onReduceChange);
        };
    }, []);

    return <BackdropCanvas ref={canvasRef} aria-hidden />;
}

/* ============================================================
   CODE INPUT — paper card slots
   ============================================================ */

interface CodeInputProps {
    value: string;
    onChange: (v: string) => void;
    length?: number;
    numeric?: boolean;
    autoFocus?: boolean;
    /** Accessible label for the underlying field (screen readers). */
    label?: string;
}

const CodeWrap = styled.div`
    position: relative;
`;

/* The real text field, kept visually hidden but focusable. It owns the hard
 * parts (paste, IME, mobile keyboard, real caret & selection); the Slots below
 * are the visible, tappable layer. Pointer events pass through so a tap can
 * target a specific box, and we place the caret there programmatically. */
const HiddenInput = styled.input`
    position: absolute;
    inset: 0;
    opacity: 0;
    border: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 2;
`;

const Slots = styled.div<{ length: number }>`
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: ${p => `repeat(${p.length}, 1fr)`};
    gap: 10px;
    cursor: text;
`;

const Slot = styled.div<{ filled: boolean }>`
    position: relative;
    aspect-ratio: 0.78;
    background: ${paper.paperCool};
    border-radius: 6px;
    box-shadow: ${p => p.filled
        ? `0 0 0 1.5px ${paper.ink}, 0 6px 14px -6px rgba(60,45,20,0.4), 0 1px 0 rgba(255,255,255,0.7) inset`
        : `0 0 0 1.5px rgba(60,45,20,0.18), 0 4px 10px -6px rgba(60,45,20,0.25), 0 1px 0 rgba(255,255,255,0.6) inset`};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: box-shadow 0.2s ${easing.standard};
`;

const SlotBaseline = styled.div<{ filled: boolean }>`
    position: absolute;
    left: 14%;
    right: 14%;
    bottom: 20%;
    height: 1.5px;
    background: ${p => p.filled ? paper.ink : "rgba(60,45,20,0.18)"};
`;

const SlotChar = styled.span`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 48px;
    color: ${paper.ink};
    letter-spacing: -0.02em;
    line-height: 1;
`;

const Cursor = styled.span`
    color: ${paper.red};
    animation: ms-blink 1s infinite;
    ${calmLoop}
`;

export function CodeInput({ value, onChange, length = 4, numeric = false, autoFocus = true, label = "Code" }: CodeInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    // Where the real caret/selection sits, mirrored from the input so the
    // rendered cursor tracks native arrow-key movement and taps.
    const [caret, setCaret] = useState(value.length);

    const sanitize = (s: string) => numeric
        ? s.replace(/[^0-9]/g, "").slice(0, length)
        : s.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, length);

    const syncCaret = () => {
        const el = inputRef.current;
        if (el) setCaret(el.selectionStart ?? value.length);
    };

    /** Tap a box → focus the field and put the caret there. Tapping a filled
     *  box selects its character so the next keystroke overwrites it; tapping
     *  an empty box (or past the end) drops the caret at the end — codes stay
     *  contiguous, no gaps. */
    const focusBox = (i: number) => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        if (i < value.length) {
            el.setSelectionRange(i, i + 1);
        } else {
            el.setSelectionRange(value.length, value.length);
        }
        syncCaret();
    };

    const chars = value.padEnd(length, " ").split("").slice(0, length);
    const caretBox = Math.min(caret, length - 1);

    return (
        <CodeWrap>
            <HiddenInput
                ref={inputRef}
                autoFocus={autoFocus}
                type="text"
                inputMode={numeric ? "numeric" : "text"}
                autoCapitalize={numeric ? "off" : "characters"}
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                aria-label={label}
                maxLength={length}
                value={value}
                onChange={(e) => { onChange(sanitize(e.target.value)); syncCaret(); }}
                onSelect={syncCaret}
                onKeyUp={syncCaret}
                onFocus={() => { setFocused(true); syncCaret(); }}
                onBlur={() => setFocused(false)}
            />
            <Slots length={length} aria-hidden="true">
                {chars.map((c, i) => {
                    const filled = c.trim() !== "";
                    const caretHere = focused && i === caretBox;
                    return (
                        <Slot
                            key={i}
                            filled={filled}
                            onPointerDown={(e) => { e.preventDefault(); focusBox(i); }}
                        >
                            <SlotBaseline filled={filled} />
                            <SlotChar>
                                {c.trim()}
                                {caretHere && <Cursor>|</Cursor>}
                            </SlotChar>
                        </Slot>
                    );
                })}
            </Slots>
        </CodeWrap>
    );
}

/* ============================================================
   TIMER RULE — a measured line across the page
   ============================================================ */

/**
 * The round's deadline, set as a ruled line rather than a panel.
 *
 * It was an `MsReceipt`: a full-width strip, perforated top and bottom, with a
 * "Build within" eyebrow and the number. Three things were wrong with that.
 * The perforation is a knockout in `paper.paper` over a `paperCool` body, so it
 * only reads as a tear when `paper.paper` is directly behind it — over the
 * backdrop's word field it read as repeating ornament. The tears ran along the
 * long edges of something that spans left-to-right, so the fiction pointed the
 * wrong way: a receipt is torn across the roll, not down it. And framing the
 * timer symmetrically top and bottom makes it a panel, which is the register
 * §2 of AGENTS.md exists to keep out.
 *
 * It also cost ~64px (14 wrap + 8 perforation + 20 padding + 22 content + 8
 * perforation) at the top of a hard-`100dvh` screen whose flexible middle is
 * already tight enough that the compose strip needs a 34vh cap. The timer was
 * spending the screen's scarcest resource on its own trim.
 *
 * So there is no container. The depleting rule *is* the line under the header,
 * the number sits at its right end like a measurement on a drawing, and the
 * label is gone — a counting-down number is self-evidently a deadline, and
 * "Build within 00:00" stops parsing at the exact moment it matters most.
 * Escalation at `warnAt` is carried by weight and colour instead of words: the
 * rule reddens and thickens, the number reddens and pulses.
 */

const TimerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: var(--content-max-wide);
    margin: 0 auto;
    /* Owns its own outer spacing. Both call sites used to declare a private
       \`TimerWrap\` and they had already drifted apart — different top padding,
       different padding at \`bp.wide\`. */
    padding: calc(var(--safe-top) + 12px) 22px 0;
    flex-shrink: 0;

    @media ${bp.wide} {
        padding: 26px 32px 0;
    }
`;

/** The rule itself: soft where time has gone, inked where it remains. */
const TimerTrack = styled.div<{ warn: boolean }>`
    flex: 1;
    position: relative;
    height: ${p => p.warn ? "3px" : "2px"};
    background: ${paper.ruleSoft};
    overflow: hidden;
    transition: height 0.3s ${easing.standard};
`;

const TimerInk = styled.div<{ pct: number; warn: boolean }>`
    position: absolute;
    inset: 0;
    width: ${p => p.pct}%;
    background: ${p => p.warn ? paper.red : paper.ink};
    transition: width 1s linear, background 0.3s ${easing.standard};
`;

/** `line-height: 1` because this is now the only thing setting the row's height. */
const TimerNum = styled.div<{ warn: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    font-size: 18px;
    line-height: 1;
    color: ${p => p.warn ? paper.red : paper.ink};
    flex-shrink: 0;
    animation: ${p => p.warn ? css`ms-tick-pulse 1s ${easing.standard} infinite` : "none"};
    ${calmLoop}
`;

/** The round's deadline, bound to the absolute `timeoutTime` the server sends. */
export function ServerTimerRule({ timeoutTime, phaseStartTime, warnAt = 10 }: {
    timeoutTime: number | null | undefined;
    phaseStartTime: number | null | undefined;
    warnAt?: number;
}) {
    // One tick per second is enough: the mm:ss only changes each second and the
    // rule glides between values via TimerInk's CSS `transition: width 1s`. The
    // old 250ms cadence re-rendered 4×/sec for no visible gain. Re-sync and tick
    // only while visible so a backgrounded tab does no timer work.
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        let id: ReturnType<typeof setInterval> | undefined;
        const start = () => {
            setNow(Date.now());
            id = setInterval(() => setNow(Date.now()), 1000);
        };
        const stop = () => { if (id) { clearInterval(id); id = undefined; } };
        const onVisibility = () => (document.visibilityState === "hidden" ? stop() : start());

        if (document.visibilityState !== "hidden") start();
        document.addEventListener("visibilitychange", onVisibility);
        return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
    }, []);

    if (!timeoutTime || !phaseStartTime) return null;

    const total = Math.max(1, Math.round((timeoutTime - phaseStartTime) / 1000));
    const remaining = Math.max(0, Math.ceil((timeoutTime - now) / 1000));
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");
    const pct = Math.max(0, (remaining / total) * 100);
    const warn = remaining <= warnAt;

    return (
        // `role="timer"` without `aria-live`: the number is worth finding on
        // demand, but announcing it every second would bury everything else.
        <TimerRow role="timer" aria-label={`${remaining} seconds left`}>
            <TimerTrack warn={warn}><TimerInk pct={pct} warn={warn} /></TimerTrack>
            <TimerNum warn={warn}>{mm}:{ss}</TimerNum>
        </TimerRow>
    );
}

/* ============================================================
   CONFETTI — paper bits for winner
   ============================================================ */

const ConfettiContainer = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 30;
`;

const ConfettiBit = styled.span`
    position: absolute;
    top: 0;
    animation-name: ms-confetti;
    animation-timing-function: ${easing.standard};
    animation-fill-mode: both;
`;

interface Bit {
    left: number;
    delay: number;
    dur: number;
    color: string;
    r0: number;
    r1: number;
    w: number;
    h: number;
}

export function Confetti({ count = 36 }: { count?: number }) {
    const reduced = useReducedMotion();
    const colors = [paper.red, paper.ink, paper.sticky, paper.sage, paper.paperDeep];
    const bitsRef = useRef<Bit[] | null>(null);
    if (!bitsRef.current) {
        bitsRef.current = Array.from({ length: count }, (_, i) => ({
            left: Math.random() * 100,
            delay: Math.random() * 0.4,
            dur: 2.4 + Math.random() * 1.4,
            color: colors[i % colors.length],
            r0: Math.random() * 360,
            r1: Math.random() * 720,
            w: 6 + Math.random() * 6,
            h: 10 + Math.random() * 8,
        }));
    }

    // Confetti is pure celebration — there's no resting state that means
    // anything, so drop it rather than paint 36 bits that snap off-screen.
    if (reduced) return null;

    return (
        <ConfettiContainer aria-hidden>
            {bitsRef.current.map((b, i) => (
                <ConfettiBit
                    key={i}
                    style={{
                        left: `${b.left}%`,
                        animationDuration: `${b.dur}s`,
                        animationDelay: `${b.delay}s`,
                        background: b.color,
                        width: b.w,
                        height: b.h,
                        ["--r0" as string]: `${b.r0}deg`,
                        ["--r1" as string]: `${b.r1}deg`,
                    }}
                />
            ))}
        </ConfettiContainer>
    );
}

/* ============================================================
   SOURCE — where the words came from, credited like print
   ============================================================ */

/**
 * The register for crediting a source: serif, italic, quiet.
 *
 * There was no shared piece for this, so all three screens that credit the
 * article invented their own. Two of them landed on serif italic — the article
 * cards and the results entry — and the word picker set it as two stacked lines
 * of uppercase sans, 11px over 9px with wide tracking.
 *
 * Uppercase was the wrong device twice over. `MsEyebrow` is for *labelling* a
 * section, and an article's name is content, not a label: uppercasing it throws
 * away the capitalisation, which for a title is information ("Emu War", "iPhone").
 * And the licence is the one legally-loaded string in the app, so setting it at
 * 9px with 0.06em tracking made the thing that most needs reading the hardest to
 * read. A printed page credits a source in italic, which the other two screens
 * had already worked out.
 *
 * Screens restyle this for weight and colour (`styled(MsSourceNote)`) rather
 * than redeclaring the family and style.
 */
export const MsSourceNote = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 13px;
    line-height: 1.4;
    color: ${paper.inkMute};
`;

/* Padding buys the hit area, the negative margin gives back the layout, so the
 * credit still sets as one line of type. Set as type this is a 13px italic word
 * — "Zinc" measured 42×19, which is a fine thing to read and a poor thing to
 * hit. The tap area is ~42×35 without the line moving. */
const SourceAnchor = styled.a`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 13px;
    color: ${paper.inkSoft};
    text-decoration-color: ${paper.ruleSoft};
    text-underline-offset: 2px;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 8px 4px;
    margin: -8px -4px;

    &:hover, &:focus-visible {
        color: ${paper.red};
        text-decoration-color: ${redA(0.5)};
    }
`;

/* The arrow is the only icon in this block, and it earns its place by saying
 * something the words don't: that the tap leaves the game. A source's own logo
 * would be the app's first vendor mark and the paper register doesn't do them. */
const SourceArrow = styled(ArrowSquareOut)`
    flex-shrink: 0;
    align-self: center;
`;

/**
 * A link to the article, opening outside the game.
 *
 * The href is the article's own, carried on the snapshot — the client never
 * works out where an article lives, so nothing here knows or cares which
 * encyclopedia it came from.
 *
 * Deliberately absent from the word picker and article selection: both run under
 * a timer, and the word picker's timeout *auto-submits*, so a tap out to read the
 * source can lose you the round. It isn't an integrity question — answers can
 * only use pool words, so the source gives nothing away — it's that leaving is
 * punished. After the round there's no clock, and "what was your article?" is the
 * question everyone actually asks.
 *
 * `target="_blank"` keeps the game's tab alive, which matters more than usual
 * when the app is installed as a PWA.
 */
export function MsSourceLink({ href, label, className }: {
    /** The article's own canonical link, as the server reported it. */
    href: string;
    label: string;
    className?: string;
}) {
    return (
        <SourceAnchor
            className={className}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => haptics.fire("select")}
        >
            {label}
            <SourceArrow size={12} weight="bold" aria-hidden />
        </SourceAnchor>
    );
}

/* ============================================================
   FADE UP — small util for screens that don't need a full paper drop
   ============================================================ */

export const MsFadeUp = styled.div<{ delay?: number }>`
    animation: ms-fade-up 0.5s ${easing.standard} both;
    ${p => p.delay !== undefined && css`animation-delay: ${p.delay}s;`}
`;

export const MsDrop = styled.div<{ delay?: number; r0?: string; r1?: string }>`
    --r0: ${p => p.r0 ?? "0deg"};
    --r1: ${p => p.r1 ?? "0deg"};
    animation: ms-paper-drop 0.7s ${easing.pop} both;
    ${p => p.delay !== undefined && css`animation-delay: ${p.delay}s;`}
`;


/* ============================================================
   PROGRESS PIPS — for entry counter on reveal
   ============================================================ */

const PipsOuter = styled.div`
    display: flex;
    gap: 6px;
`;

const Pip = styled.div<{ active: boolean; current: boolean }>`
    height: 4px;
    width: ${p => p.current ? 36 : 12}px;
    background: ${p => p.active ? paper.red : "rgba(60,45,20,0.18)"};
    border-radius: 2px;
    transition: all 0.4s ${easing.standard};
`;

export function ProgressPips({ count, index }: { count: number; index: number }) {
    return (
        <PipsOuter>
            {Array.from({ length: count }).map((_, i) => (
                <Pip key={i} active={i <= index} current={i === index} />
            ))}
        </PipsOuter>
    );
}

/* ============================================================
   UTILITIES
   ============================================================ */

export const Spacer = styled.div<{ h?: number }>`
    height: ${p => p.h ?? 12}px;
`;

/* Custom layout primitives — filter our short-name props so they don't leak
 * to the DOM as unknown attributes. */
const stackConfig = {
    shouldForwardProp: (prop: string) => !["gap", "align"].includes(prop),
};
const rowConfig = {
    shouldForwardProp: (prop: string) => !["gap", "justify", "align", "wrap"].includes(prop),
};

export const Stack = styled("div", stackConfig)<{ gap?: number; align?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${p => p.gap ?? 12}px;
    ${p => p.align && css`align-items: ${p.align};`}
`;

export const Row = styled("div", rowConfig)<{ gap?: number; justify?: string; align?: string; wrap?: boolean }>`
    display: flex;
    gap: ${p => p.gap ?? 8}px;
    ${p => p.justify && css`justify-content: ${p.justify};`}
    ${p => p.align && css`align-items: ${p.align};`}
    ${p => p.wrap && css`flex-wrap: wrap;`}
`;

export const Centered = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
`;

const splitGridConfig = {
    shouldForwardProp: (prop: string) => !["gap", "align", "padTop"].includes(prop),
};

/**
 * The desktop split: one column on phone, two unequal ones once there's room —
 * hero beside the how-to-play card on the home screen, room code beside the
 * roster in the lobby. The 1.1fr / 0.9fr bias is the point: the left column
 * leads, so it gets the extra tenth rather than the two sitting evenly.
 *
 * Both screens had declared this media query themselves, byte-identical, and
 * differed only in the three things now passed as props.
 */
export const MsSplitGrid = styled("div", splitGridConfig)<{
    gap?: number;
    align?: string;
    padTop?: number;
}>`
    display: flex;
    flex-direction: column;
    gap: ${p => p.gap ?? 16}px;

    @media ${bp.split} {
        display: grid;
        grid-template-columns: 1.1fr 0.9fr;
        gap: 48px;
        align-items: ${p => p.align ?? "start"};
        padding-top: ${p => p.padTop ?? 0}px;
    }
`;

/** Pass-through for extra HTML props on a div */
export type DivProps = HTMLAttributes<HTMLDivElement>;
